'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';

const PUBLIC_NAV_ITEMS = [
  { label: 'ABOUT', href: '/about' },
  { label: 'DISCOVER', href: '/discover' },
  { label: 'ASSESS', href: '/assess' },
  { label: 'INDUSTRY', href: '/industry-knowledge' },
  { label: 'KNOWLEDGE', href: '/knowledge-center' },
];

const AUTH_NAV_ITEMS = [
  { label: 'QUIZ', href: '/quiz' },
  { label: 'MY DASHBOARD', href: '/dashboard' },
];

export default function Navigation() {
  const pathname = usePathname();
  const { user, loading } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const isActive = (href: string) => {
    if (href === '/discover' && pathname === '/') return true;
    return pathname === href || pathname.startsWith(href + '/');
  };

  const navItems = [
    ...PUBLIC_NAV_ITEMS,
    ...(user ? AUTH_NAV_ITEMS : [{ label: 'QUIZ', href: '/quiz' }]),
    { label: 'ADMIN', href: '/admin' },
  ];

  const renderLink = (item: { label: string; href: string }, keyPrefix: string) => (
    <Link
      key={`${keyPrefix}-${item.href}`}
      href={item.href}
      className="relative px-4 py-2 text-xs font-semibold tracking-widest transition-colors duration-150 no-underline"
      style={{
        color: isActive(item.href) ? '#FF6B6B' : '#A8B2C1',
        borderBottom: isActive(item.href) ? '2px solid #FF6B6B' : '2px solid transparent',
        letterSpacing: '0.1em',
      }}
    >
      {item.label}
    </Link>
  );

  const renderMobileLink = (item: { label: string; href: string }, keyPrefix: string) => (
    <Link
      key={`${keyPrefix}-${item.href}`}
      href={item.href}
      className="px-3 py-3 text-sm font-semibold tracking-widest rounded transition-colors no-underline"
      style={{
        color: isActive(item.href) ? '#FF6B6B' : '#A8B2C1',
        background: isActive(item.href) ? 'rgba(255,107,107,0.08)' : 'transparent',
        letterSpacing: '0.08em',
        borderLeft: isActive(item.href) ? '2px solid #FF6B6B' : '2px solid transparent',
      }}
    >
      {item.label}
    </Link>
  );

  return (
    <>
      <nav className="sticky top-0 z-50 w-full" style={{ background: '#0D1B2A', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 2xl:px-16">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link href="/discover" className="flex items-center gap-3 no-underline">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded flex items-center justify-center" style={{ background: 'rgba(255,107,107,0.15)', border: '1px solid rgba(255,107,107,0.3)' }}>
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <polygon points="9,1 16.5,5 16.5,13 9,17 1.5,13 1.5,5" stroke="#FF6B6B" strokeWidth="1.5" fill="none"/>
                    <polygon points="9,5 13,7.5 13,12.5 9,15 5,12.5 5,7.5" fill="rgba(255,107,107,0.3)"/>
                  </svg>
                </div>
                <div className="flex flex-col leading-tight">
                  <span className="font-mono text-base font-bold tracking-tight" style={{ color: '#FF6B6B' }}>SMARCK-M</span>
                  <span className="text-xs font-medium tracking-wider" style={{ color: '#4ECDC4', fontSize: '9px' }}>ENTERPRISE PLATFORM</span>
                </div>
              </div>
            </Link>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-1">
              {PUBLIC_NAV_ITEMS.map((item) => renderLink(item, 'nav'))}
              {!loading && user && AUTH_NAV_ITEMS.map((item) => renderLink(item, 'nav'))}
              {!loading && !user && renderLink({ label: 'QUIZ', href: '/quiz' }, 'nav')}
              {renderLink({ label: 'ADMIN', href: '/admin' }, 'nav')}
            </div>

            {/* Mobile hamburger */}
            <button
              className="md:hidden flex flex-col gap-1.5 p-2 rounded transition-colors"
              style={{ color: '#A8B2C1' }}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle navigation menu"
            >
              <span className="block w-5 h-0.5 transition-all duration-200"
                style={{ background: '#A8B2C1', transform: mobileOpen ? 'translateY(8px) rotate(45deg)' : 'none' }} />
              <span className="block w-5 h-0.5 transition-all duration-200"
                style={{ background: '#A8B2C1', opacity: mobileOpen ? 0 : 1 }} />
              <span className="block w-5 h-0.5 transition-all duration-200"
                style={{ background: '#A8B2C1', transform: mobileOpen ? 'translateY(-8px) rotate(-45deg)' : 'none' }} />
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        <div className="md:hidden overflow-hidden transition-all duration-300"
          style={{ maxHeight: mobileOpen ? '500px' : '0px', borderTop: mobileOpen ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
          <div className="px-6 py-4 flex flex-col gap-1" style={{ background: '#0D1B2A' }}>
            {PUBLIC_NAV_ITEMS.map((item) => renderMobileLink(item, 'mobile'))}
            {!loading && user && AUTH_NAV_ITEMS.map((item) => renderMobileLink(item, 'mobile'))}
            {!loading && !user && renderMobileLink({ label: 'QUIZ', href: '/quiz' }, 'mobile')}
            {renderMobileLink({ label: 'ADMIN', href: '/admin' }, 'mobile')}
          </div>
        </div>
      </nav>
    </>
  );
}